import { useState } from 'react';

import {
  useOrganizationPackageLabels,
  usePackageLabelAssignments,
  useUpdatePackageLabelAssignments,
} from '../../../hooks/api/packageLabels';
import { Tab, type VersionedLabelOption } from './types';
import { useTabs } from '../../generic';
import { reduceAssignmentsToVersionMap } from './utils';
import { useAddToast } from '../../../contexts/ToastContext';
import { useHasPermission, type Permission, Action, ResourceType } from '../../../shared/permissions';

/**
 * overall state hook for the package label modal encompassing all tabs and updates
 */
export const usePackageLabelState = (packageId: string) => {
  const tabs = useTabs({ tabIds: Object.values(Tab), defaultTab: Tab.Global });
  const addToast = useAddToast();
  const hasPermission = useHasPermission();

  /**
   * The user must have global permissions to edit global scoped labels.
   * If the user has global OR local permissions, then they can edit project and revision scoped labels.
   */
  const permissions: Permission[] = [
    { action: Action.Set, resourceType: ResourceType.PackageLabel },
    { action: Action.SetLocal, resourceType: ResourceType.PackageLabel, projectId: packageId },
  ];
  const hasNoPermission = !permissions.some((permission) => hasPermission(permission));

  // init API hooks
  const { data: labels, isLoading: isLoadingLabels } = useOrganizationPackageLabels();
  const { data: labelAssignments, isLoading: isLoadingLabelAssignments } = usePackageLabelAssignments(packageId);
  const { updatePackageLabelAssignments } = useUpdatePackageLabelAssignments(packageId);

  // set up state for selected labels in each scope
  const [globalLabelsSelected, setGlobalLabelsSelected] = useState<VersionedLabelOption>({});
  const [projectLabelsSelected, setProjectLabelsSelected] = useState<VersionedLabelOption>({});
  const [revisionLabelsSelected, setRevisionLabelsSelected] = useState<VersionedLabelOption>({});

  // helper functions to get the initial assignments for each scope before any changes are made
  const getInitialGlobalAssignments = () => {
    return (
      labelAssignments?.packageLabelAssignments
        .filter((assignment) => assignment.scope === 'org')
        .reduce(reduceAssignmentsToVersionMap, {} as VersionedLabelOption) || {}
    );
  };

  const getInitialProjectAssignments = (parentProjectLocator: string) => {
    return (
      labelAssignments?.packageLabelAssignments
        .filter((assignment) => assignment.scope === 'project' && assignment.scopeId === parentProjectLocator)
        .reduce(reduceAssignmentsToVersionMap, {} as VersionedLabelOption) || {}
    );
  };

  const getInitialRevisionAssignments = (parentRevisionLocator: string) => {
    return (
      labelAssignments?.packageLabelAssignments
        .filter((assignment) => assignment.scope === 'revision' && assignment.scopeId === parentRevisionLocator)
        .reduce(reduceAssignmentsToVersionMap, {} as VersionedLabelOption) || {}
    );
  };

  const initializeGlobalSelected = () => {
    setGlobalLabelsSelected(getInitialGlobalAssignments());
  };
  const initializeProjectSelected = (parentProjectLocator: string) => {
    setProjectLabelsSelected(getInitialProjectAssignments(parentProjectLocator));
  };
  const initializeRevisionSelected = (parentRevisionLocator: string) => {
    setRevisionLabelsSelected(getInitialRevisionAssignments(parentRevisionLocator));
  };

  const onSaveSuccess = () => {
    window.location.reload();
  };

  // save functions for each scope
  const onSaveGlobal = () => {
    updatePackageLabelAssignments.mutate(
      {
        newLabelIds: globalLabelsSelected,
        scope: 'org',
      },
      {
        onSuccess() {
          onSaveSuccess();
        },
        onError(error) {
          addToast({ type: 'error', content: `Error updating global labels: ${error.data.message}` });
        },
      },
    );
  };

  const onSaveProject = (parentProjectLocator: string) => {
    updatePackageLabelAssignments.mutate(
      {
        newLabelIds: projectLabelsSelected,
        scope: 'project',
        scopeId: parentProjectLocator,
      },
      {
        onSuccess() {
          onSaveSuccess();
        },
        onError(error) {
          addToast({ type: 'error', content: `Error updating project labels: ${error.text}` });
        },
      },
    );
  };

  const onSaveRevision = (parentRevisionLocator: string) => {
    updatePackageLabelAssignments.mutate(
      {
        newLabelIds: revisionLabelsSelected,
        scope: 'revision',
        scopeId: parentRevisionLocator,
      },
      {
        onSuccess() {
          onSaveSuccess();
        },
        onError(error) {
          addToast({ type: 'error', content: `Error updating revision labels: ${error.data.message}` });
        },
      },
    );
  };

  return {
    tabs,
    getInitialGlobalAssignments,
    globalLabelsSelected,
    setGlobalLabelsSelected,
    initializeGlobalSelected,
    getInitialProjectAssignments,
    projectLabelsSelected,
    setProjectLabelsSelected,
    initializeProjectSelected,
    getInitialRevisionAssignments,
    revisionLabelsSelected,
    setRevisionLabelsSelected,
    initializeRevisionSelected,
    labels,
    isLoadingLabels,
    labelAssignments,
    isLoadingLabelAssignments,
    onSaveGlobal,
    onSaveProject,
    onSaveRevision,
    hasNoPermission,
  };
};
